import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService, OAuthSuccessEvent } from 'angular-oauth2-oidc';
import { BehaviorSubject, filter, firstValueFrom,  map, Observable, ReplaySubject } from 'rxjs';
import { IdentityProviderIdentifier } from '../config/oauth2/idp.config';
import { environment } from '../../environments/environment';
import { Profile } from '../model/profile.type';
import { handleString } from '../util/string/handle-string';
import { Tenant } from '../model/dto/tenant.type';
import { HttpClient } from '@angular/common/http';
import { User } from '../model/dto/user.type';

const SESSION_STORAGE_IDP_KEY = 'idp';

function isNotNull<T>(value: T): value is NonNullable<T> {
    return value !== null;
}

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    private isAuthenticationInitialized$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private userTenants: BehaviorSubject<Tenant[]> = new BehaviorSubject<Tenant[]>([]);
    public userTenants$: Observable<Tenant[]> = this.userTenants.asObservable();
    public selectedTenantId$ = new ReplaySubject<string>(1);
    public userInfo$= new ReplaySubject<User>(1);

    constructor(private oauthService: OAuthService, public router: Router, private client: HttpClient) {
        this.selectedTenantId$.subscribe((tenant: string) => {
            sessionStorage.setItem('current_tenant', tenant);
            this.fetchUserInfo();
        });
    }

    public logout(): void {
        this.isAuthenticationInitialized$.next(false);
        sessionStorage.clear();
        this.router.navigateByUrl('/auth/login');
    }

    public async useIdpAndLoadDiscoveryDocument(
        idp: IdentityProviderIdentifier,
        persist: boolean = true,
    ): Promise<OAuthSuccessEvent> {
        if (environment.identityProviders[idp] === undefined) {
            console.error('Missing idp configuration');
            return Promise.reject();
        }
        if (persist) {
            sessionStorage.setItem(SESSION_STORAGE_IDP_KEY, idp);
        }
        this.oauthService.configure(environment.identityProviders[idp]!.authConfig);
        this.oauthService.setupAutomaticSilentRefresh();
        const oAuthSuccessEventPromise = await this.oauthService.loadDiscoveryDocument();

        this.isAuthenticationInitialized$.next(true);
        return oAuthSuccessEventPromise;
    }

    public login(): void {
        this.oauthService.initCodeFlow();
    }

    public getToken(): string | undefined {
        return this.oauthService.getIdToken();
    }

    public initializeTenant(): void {
        const claimTenants = this.getTenantsFromClaims();

        this.client.get<Tenant[]>(`${environment.backendUrl}/tenant/v1`)
            .pipe(map((tenants: Tenant[]) => {
                claimTenants.forEach((claimTenant: Tenant) => {
                    const tenant = tenants.find((t: Tenant) => t.id === claimTenant.id);

                    if (tenant) {
                        tenant.name = claimTenant.name;
                    } else {
                        tenants.push(claimTenant);
                    }
                });

                this.userTenants.next(tenants);
                if (tenants.length === 0) return null;

                const storedTenantId = this.getStoredTenantId();
                const tenant = tenants.find((t: Tenant) => t.id === storedTenantId)
                    ?? null;

                return tenant?.id || tenants[0].id;
            }))
            .pipe(filter(isNotNull))
            .subscribe(this.selectedTenantId$.next.bind(this.selectedTenantId$));
    }

    private fetchUserInfo(): Observable<User> {
        this.client.get<User>(`${environment.backendUrl}/users/v1/me`).subscribe(this.userInfo$.next.bind(this.userInfo$));

        return this.userInfo$;
    }

    /**
     * Initializes current authentication with the used idp and tries to perform a login. This should be run at startup
     * If no idp has been used yet, nothing will happen
     */
    public async initAuthentication(): Promise<void> {
        const item = sessionStorage.getItem(SESSION_STORAGE_IDP_KEY);

        if (item !== null && item in environment.identityProviders) {
            await this.useIdpAndLoadDiscoveryDocument(item as IdentityProviderIdentifier, false);
        }
    }

    public async tryLoginFromRedirect(): Promise<boolean> {
        // we need this because there is a race condition when we initialize in appComponent and are also trying the login in the redirect component
        const isInitialized = await firstValueFrom(this.isAuthenticationInitialized$);

        if (!isInitialized) return false;
        return this.oauthService.tryLogin();
    }

    public isLoggedIn(): boolean {
        return this.oauthService.hasValidAccessToken();
    }

    public getProfile(): Profile {
        const identityClaims = this.oauthService.getIdentityClaims();

        // ToDo Admin Roles
        return {
            email: identityClaims['email'],
            name: identityClaims['name'],
            tenants: this.getTenantsFromClaims(),
        };
    }

    public getStoredTenantId(): string | null {
        return sessionStorage.getItem('current_tenant');
    }

    private getTenantsFromClaims(): Tenant[] {
        // I'd rather pass this in as a param, but it has type Record<string,any> and this throws errors.
        const claims = this.oauthService.getIdentityClaims();

        if (!claims || !claims['roles'] || claims['roles'].length === 0) return [];

        const tenants: Set<string> = new Set<string>();

        claims['roles'].map(handleString).map((tenant: string) => tenants.add(tenant));

        return Array.from(tenants).map((tenant: string) => ({
            id: tenant,
            name: tenant,
        }));
    }
}
